import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Filter, Plus, Trash2, ExternalLink } from "lucide-react";
import api, { formatApiError } from "../lib/api";

// Per-org URL exclusion list — pages the client doesn't want Mission Watch
// scoring against. Applied server-side in services/exclusions.py before a
// report is persisted, so dashboards, alerts, and the score all reflect
// the filtered view.

export default function MonitoringExclusions() {
  const [items, setItems] = useState(null); // null while loading
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/organization/exclusions")
      .then(({ data }) => { if (mounted) setItems(data?.exclusions || []); })
      .catch(() => { if (mounted) setItems([]); });
    return () => { mounted = false; };
  }, []);

  const add = async () => {
    const v = input.trim();
    if (!v) return;
    setBusy(true);
    try {
      const { data } = await api.post("/organization/exclusions", { url: v });
      setItems(data.exclusions);
      setInput("");
      toast.success("Exclusion added. It will apply on your next scan.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not add exclusion.");
    } finally { setBusy(false); }
  };

  const remove = async (url) => {
    setBusy(true);
    try {
      const { data } = await api.delete("/organization/exclusions", { data: { url } });
      setItems(data.exclusions);
      toast.success("Exclusion removed.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove exclusion.");
    } finally { setBusy(false); }
  };

  if (items === null) {
    return (
      <section
        className="mt-6 rounded-2xl bg-white border border-black/5 shadow-soft p-6"
        data-testid="exclusions-card"
      >
        <p className="text-sm text-charcoal-400 font-mono">Loading exclusions…</p>
      </section>
    );
  }

  return (
    <section
      className="mt-6 rounded-2xl bg-white border border-black/5 shadow-soft p-6"
      data-testid="exclusions-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <Filter className="w-4 h-4 text-brand" />
        <h2 className="text-lg font-medium text-charcoal-900">Excluded pages</h2>
      </div>
      <p className="text-sm text-charcoal-500 mb-4 leading-relaxed">
        Add URLs (or patterns with <code className="px-1 py-0.5 rounded bg-charcoal-100 text-[11px] font-mono">*</code> wildcards) that Mission Watch should ignore when scoring. Useful for retired campaigns, staging links, and pages you don&apos;t publish but Mission Watch still discovers. Excluded items keep appearing in diagnostics but no longer affect your Mission Watch Score or trigger alerts.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder="e.g. https://your-site.org/old-campaign/* or a full URL"
          className="flex-1 h-10 px-3 rounded-xl border border-black/10 bg-white text-sm placeholder:text-charcoal-400 focus:outline-none focus:ring-2 focus:ring-brand/40"
          data-testid="exclusion-input"
        />
        <button
          type="button"
          onClick={add}
          disabled={busy || !input.trim()}
          className="inline-flex items-center justify-center gap-1.5 h-10 px-5 rounded-xl bg-charcoal-900 text-white text-sm font-medium hover:bg-charcoal-800 transition-colors disabled:opacity-60"
          data-testid="exclusion-add-btn"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>

      {items.length === 0 ? (
        <div
          className="text-xs font-mono text-charcoal-400 py-3 px-3.5 rounded-xl bg-charcoal-50/50 border border-dashed border-black/10"
          data-testid="exclusion-empty"
        >
          No exclusions yet. Every page Mission Watch discovers is scored.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="exclusion-list">
          {items.map((url) => (
            <li
              key={url}
              className="flex items-center justify-between gap-3 py-2.5 px-3.5 rounded-xl border border-black/5 bg-charcoal-50/50"
              data-testid={`exclusion-row-${url}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <ExternalLink className="w-3.5 h-3.5 text-charcoal-400 shrink-0" />
                <code className="text-xs font-mono text-charcoal-700 truncate" title={url}>
                  {url}
                </code>
              </div>
              <button
                type="button"
                onClick={() => remove(url)}
                disabled={busy}
                className="shrink-0 inline-flex items-center gap-1 text-xs text-charcoal-500 hover:text-red-500 transition-colors disabled:opacity-60"
                data-testid={`exclusion-remove-${url}`}
                aria-label={`Remove exclusion ${url}`}
              >
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-4 text-[11px] font-mono text-charcoal-400 leading-relaxed">
        Exclusions apply on your next scan. Critical checks (website uptime, SSL, DNS, primary donation page) can never be silenced by exclusions.
      </p>
    </section>
  );
}
